// Shows the vehicle context on the filtered collection results page: a banner at the top
// confirming the fitment, plus a "Fits <vehicle>" line under every product card that ACTUALLY
// fits — checked per card (vendor/tags/bolt pattern/size), not assumed. Runs when ?vehicle=...
// is present (set by vehicle-shop-selector.js), or falls back to whatever vehicle is already
// stored — so it keeps showing on later visits without the customer re-entering it, same as the
// product-page fitment widget already does.
//
// This mirrors the fit-check logic in vehicle-fitment-selector.js (the product-page widget) —
// a real bug once shipped here because this file used to show "Fits <vehicle>" unconditionally
// on every card, which was wrong on pages the native collection filters don't narrow by vehicle
// at all (e.g. a single-brand page like /collections/aodhan-wheels): a product could show
// "Fits" in the grid and "Does not fit" the moment you opened it. Keep the two in sync if either
// changes.
(() => {
    const VEHICLE_STORAGE_KEY = 'unleashed:selected-vehicle';

    const normalizeBoltPattern = (s) => String(s).toUpperCase().replace(/\s+/g, '');
    const isBlankBoltPattern = (s) => normalizeBoltPattern(s).includes('BLANK');

    const vehicleCoversYear = (v, year) => {
        if (v.year_start == null) return false;
        if (year < v.year_start) return false;
        if (v.year_end === 'current') return true;
        if (v.year_end == null) return year === v.year_start;
        return year <= v.year_end;
    };

    function getStoredVehicle() {
        try {
            const raw = localStorage.getItem(VEHICLE_STORAGE_KEY);
            return raw ? JSON.parse(raw) : null;
        } catch (e) {
            return null;
        }
    }

    const storedVehicle = getStoredVehicle();
    const urlVehicleLabel = new URLSearchParams(window.location.search).get('vehicle');
    if (!storedVehicle?.year || !storedVehicle?.make || !storedVehicle?.model) return;

    const vehicleLabel = urlVehicleLabel || `${storedVehicle.year} ${storedVehicle.make} ${storedVehicle.model}`;

    const CHECK_SVG = '<svg viewBox="0 0 512 512" width="14" height="14" fill="currentColor" aria-hidden="true"><path d="M470.6 105.4c12.5 12.5 12.5 32.8 0 45.3l-256 256c-12.5 12.5-32.8 12.5-45.3 0l-128-128c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0L192 338.7 425.4 105.4c12.5-12.5 32.8-12.5 45.3 0z"/></svg>';
    const INFO_SVG = '<svg viewBox="0 0 512 512" width="14" height="14" fill="currentColor" aria-hidden="true"><path d="M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM216 336h24V272H216c-13.3 0-24-10.7-24-24s10.7-24 24-24h48c13.3 0 24 10.7 24 24v88h8c13.3 0 24 10.7 24 24s-10.7 24-24 24H216c-13.3 0-24-10.7-24-24s10.7-24 24-24zm40-208a32 32 0 1 1 0 64 32 32 0 1 1 0-64z"/></svg>';
    const X_SVG = '<svg viewBox="0 0 384 512" width="14" height="14" fill="currentColor" aria-hidden="true"><path d="M342.6 150.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L192 210.7 86.6 105.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L146.7 256 41.4 361.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L192 301.3 297.4 406.6c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L237.3 256 342.6 150.6z"/></svg>';

    function escapeHtml(str) {
        const div = document.createElement('div');
        div.textContent = str;
        return div.innerHTML;
    }

    function clearVehicleFit() {
        // If this page IS the vehicle's own dedicated curated collection (e.g.
        // /collections/widebody-challenger-charger-wheels), reloading the same path after
        // clearing the vehicle would just leave the customer stuck on that narrow, fixed-rule
        // collection with no vehicle context anymore — send them back to the general Wheels
        // catalog instead. Any other page (a brand page, the general Wheels page itself) just
        // reloads in place, same as before.
        const isDedicatedCollection = storedVehicle?.restrictedCollectionHandle
            && window.location.pathname === `/collections/${storedVehicle.restrictedCollectionHandle}`;
        try {
            localStorage.removeItem(VEHICLE_STORAGE_KEY);
        } catch (e) {
            // ignore
        }
        window.location.href = isDedicatedCollection ? '/collections/wheels' : window.location.pathname;
    }

    function insertBanner() {
        if (document.querySelector('.vehicle-fit-banner')) return;
        const inner = Array.from(document.querySelectorAll('[class]')).find(el => el.classList.contains('#collection-inner'));
        if (!inner) return;

        const banner = document.createElement('div');
        banner.className = 'vehicle-fit-banner';
        banner.innerHTML = `${CHECK_SVG}<span class="vehicle-fit-banner-text">These wheels fit your <strong>${escapeHtml(vehicleLabel)}</strong></span><button type="button" class="vehicle-fit-banner-clear">Clear filter</button>`;
        banner.querySelector('.vehicle-fit-banner-clear').addEventListener('click', clearVehicleFit);
        inner.insertBefore(banner, inner.firstChild);
    }

    // Downgrades the banner once we know none of the products actually on this page (e.g. a
    // single-brand page like /collections/rotiform-wheels) fit the selected vehicle — the
    // banner otherwise keeps asserting a blanket "these fit" that the per-card badges below it
    // visibly contradict. Only fires once we have a real true/false answer for at least one
    // card; stays on the default wording while that's still unknown.
    function updateBannerForResults(anyFits, anyChecked, anyVerified, anyUnverified) {
        const banner = document.querySelector('.vehicle-fit-banner');
        if (!banner || !anyChecked) return;
        const textEl = banner.querySelector('.vehicle-fit-banner-text');
        if (!textEl) return;
        const label = `<strong>${escapeHtml(vehicleLabel)}</strong>`;
        // Only touch the DOM when the state actually changes — this runs from a MutationObserver,
        // so rewriting the banner every time would re-trigger itself forever.
        const state = !anyFits ? 'no-fit' : (anyUnverified && !anyVerified ? 'unverified-all' : (anyUnverified ? 'unverified-mixed' : 'fits'));
        if (banner.dataset.fitState === state) return;
        banner.dataset.fitState = state;
        banner.classList.remove('vehicle-fit-banner--no-fit', 'vehicle-fit-banner--unverified');
        if (!anyFits) {
            banner.classList.add('vehicle-fit-banner--no-fit');
            textEl.innerHTML = `None of these wheels fit your ${label}`;
        } else if (anyUnverified && !anyVerified) {
            // Off-road wheels: the bolt pattern matches but offset/width vary too much to verify.
            banner.classList.add('vehicle-fit-banner--unverified');
            textEl.innerHTML = `These wheels match the bolt pattern of your ${label}. Our team confirms offset and fitment before shipping &mdash; call <a href="tel:8773774229">(877) 377-4229</a>.`;
        } else if (anyUnverified) {
            banner.classList.add('vehicle-fit-banner--unverified');
            textEl.innerHTML = `Wheels marked "Fits" are verified for your ${label}. The others match its bolt pattern only; our team confirms offset and fitment before shipping &mdash; call <a href="tel:8773774229">(877) 377-4229</a>.`;
        } else {
            textEl.innerHTML = `These wheels fit your ${label}`;
        }
    }

    function hideActiveFilters() {
        const el = Array.from(document.querySelectorAll('[class]')).find(el => el.classList.contains('#collection-active-filters'));
        if (el) el.style.display = 'none';
    }

    function readJSONScript(el) {
        if (!el) return null;
        try {
            return JSON.parse(el.textContent);
        } catch (e) {
            return null;
        }
    }

    let vehicles = null;
    let fetchStarted = false;

    async function loadVehicles() {
        if (fetchStarted) return;
        fetchStarted = true;
        const srcEl = document.querySelector('[data-vehicle-fitment-src]');
        const src = readJSONScript(srcEl);
        if (!src) return;
        try {
            const res = await fetch(src);
            vehicles = await res.json();
        } catch (e) {
            console.error('Vehicle fitment data failed to load', e);
        }
    }

    function findVehicle() {
        // A vehicle picked from the 4Play-only list carries its real bolt patterns (from the
        // vendor sheet) — use those directly so per-card fit badges work for trucks the car
        // database doesn't contain, and so they match what the search bar filtered by.
        if (['4play', 'offroad'].includes(storedVehicle.fitmentSource) && storedVehicle.boltPatterns?.length) {
            return { bolt_patterns: storedVehicle.boltPatterns };
        }
        if (!vehicles) return null;
        return vehicles.find(v => v.make === storedVehicle.make && v.model === storedVehicle.model
            && vehicleCoversYear(v, storedVehicle.year)) || null;
    }

    // A curated vehicle names compatible products either by tag (specific models) or by whole
    // vendor, with either being enough UNLESS restricted_match_mode is 'and' (both required) —
    // see vehicle-fitment-selector.js for the full rationale.
    function curatedMatch(full, vendor, tags) {
        if (!full.restricted_tags?.length && !full.restricted_vendors?.length) return false;
        const tagMatch = full.restricted_tags?.length && tags.some(t => full.restricted_tags.includes(t));
        const vendorMatch = full.restricted_vendors?.length && full.restricted_vendors.includes(vendor);
        if (full.restricted_match_mode === 'and') return Boolean(tagMatch && vendorMatch);
        return Boolean(tagMatch || vendorMatch);
    }

    function productDiameters(diameterValues, tags) {
        if (diameterValues?.length) {
            return diameterValues
                .map(v => {
                    const m = /(\d+(?:\.\d+)?)/.exec(String(v));
                    return m ? parseFloat(m[1]) : null;
                })
                .filter(d => d != null);
        }
        return tags
            .map(t => /^wheel size:\s*(\d+(?:\.\d+)?)$/i.exec(t.trim()))
            .filter(Boolean)
            .map(m => parseFloat(m[1]));
    }

    function fitsSizeRange(full, diameters) {
        if (full.wheel_diameter_min == null || full.wheel_diameter_max == null) return null;
        if (!diameters.length) return null;
        return diameters.some(d => d >= full.wheel_diameter_min && d <= full.wheel_diameter_max);
    }

    function fitsThisProduct(full, vendor, tags, boltPatternValues, diameterValues) {
        const productBoltPatterns = (boltPatternValues || []).map(normalizeBoltPattern);

        if (full.restricted_tags?.length || full.restricted_vendors?.length) {
            if (curatedMatch(full, vendor, tags)) return true;
            if (full.restricted_match_mode === 'and') {
                const vendorMatch = full.restricted_vendors.includes(vendor);
                if (!vendorMatch) return false;
            } else if (full.restricted_strict) {
                return false;
            }
        }

        const boltPatterns = Array.from(new Set((full.bolt_patterns || []).map(normalizeBoltPattern)));
        if (!productBoltPatterns.length || !boltPatterns.length) return null;

        const boltFits = productBoltPatterns.some(isBlankBoltPattern)
            || boltPatterns.some(bp => productBoltPatterns.includes(bp));
        if (!boltFits) return false;

        const diameters = productDiameters(diameterValues, tags);
        return fitsSizeRange(full, diameters) !== false;
    }

    // true/false/null — null means "unknown" (e.g. missing bolt-pattern data), which shows no
    // badge at all rather than guessing either way.
    function cardFits(card, full) {
        const vendor = readJSONScript(card.querySelector('[data-vehicle-fit-vendor-json]')) || '';
        const tags = readJSONScript(card.querySelector('[data-vehicle-fit-tags-json]')) || [];
        const boltPatternValues = readJSONScript(card.querySelector('[data-vehicle-fit-bolt-patterns-json]')) || [];
        const diameterValues = readJSONScript(card.querySelector('[data-vehicle-fit-diameters-json]')) || [];
        return fitsThisProduct(full, vendor, tags, boltPatternValues, diameterValues);
    }

    // Off-road wheels (product type "Offroad Wheels") can match a vehicle's bolt pattern while the
    // offset/width still won't work, so "Fits" is only claimed for 4Play wheels when the vehicle
    // came from 4Play's own vendor fitment list. Everything else off-road shows "unverified".
    function cardUnverified(card) {
        const type = readJSONScript(card.querySelector('[data-vehicle-fit-type-json]')) || '';
        if (type !== 'Offroad Wheels') return false;
        const vendor = readJSONScript(card.querySelector('[data-vehicle-fit-vendor-json]')) || '';
        return !(vendor === '4Play Wheels' && storedVehicle.fitmentSource === '4play');
    }

    // Accessories (lug nuts, spacers, hub rings, valve stems, ...) aren't wheels, so a wheel
    // "fits / doesn't fit your vehicle" verdict doesn't apply to them. Matched by product type
    // "Accessory", or by the "accessories" tag — a few accessories have a blank product type
    // but still carry the tag. Keep in sync with s_product.liquid, which hides the product-page
    // fitment widget for the same products.
    function isAccessoryCard(card) {
        const type = String(readJSONScript(card.querySelector('[data-vehicle-fit-type-json]')) || '').trim().toLowerCase();
        if (type === 'accessory' || type === 'accessories') return true;
        const tags = readJSONScript(card.querySelector('[data-vehicle-fit-tags-json]')) || [];
        return tags.some(t => String(t).trim().toLowerCase() === 'accessories');
    }

    async function insertCardBadges() {
        const allCards = Array.from(document.querySelectorAll('[data-product-id]')).filter(c => c.classList.contains('#product-card'));
        if (!allCards.length) return;

        const cards = allCards.filter(c => !isAccessoryCard(c));
        if (!cards.length) {
            // A page of nothing but accessories — there's no wheel here to verify, so no
            // "these wheels fit / don't fit" banner either.
            document.querySelector('.vehicle-fit-banner')?.remove();
            return;
        }
        // Wheels are on the page (again, e.g. after a filter change) — make sure the banner exists.
        insertBanner();

        await loadVehicles();
        const full = findVehicle();
        if (!full) return; // no fitment data for this vehicle — don't guess

        // Whether the banner should say "these fit" vs "none of these fit" depends on every
        // card currently on the page, not just the ones getting a badge on this pass — a card
        // badged on an earlier tick still counts toward the total.
        let anyFits = false;
        let anyChecked = false;
        let anyVerified = false;
        let anyUnverified = false;

        cards.forEach(card => {
            const existingBadge = card.querySelector('.vehicle-fit-card-badge');
            if (existingBadge) {
                anyChecked = true;
                if (!existingBadge.classList.contains('vehicle-fit-card-badge--no-fit')) {
                    anyFits = true;
                    if (existingBadge.classList.contains('vehicle-fit-card-badge--unverified')) anyUnverified = true;
                    else anyVerified = true;
                }
                return;
            }
            const fits = cardFits(card, full);
            if (fits == null) return;
            anyChecked = true;
            const unverified = Boolean(fits) && cardUnverified(card);
            if (fits) {
                anyFits = true;
                if (unverified) anyUnverified = true; else anyVerified = true;
            }

            const title = card.querySelector('.\\#product-card-title');
            if (!title) return;

            // Insert after the TargetBay star rating when present (see tb-shopify.liquid),
            // so the order reads title -> stars -> fit badge -> price either way.
            const starContainer = card.querySelector('.targetbay_star_container');
            const insertAfter = starContainer || title;

            const badge = document.createElement('p');
            badge.className = !fits
                ? 'vehicle-fit-card-badge vehicle-fit-card-badge--no-fit'
                : (unverified ? 'vehicle-fit-card-badge vehicle-fit-card-badge--unverified' : 'vehicle-fit-card-badge');
            badge.innerHTML = !fits
                ? `${X_SVG}<span>Doesn't fit ${escapeHtml(vehicleLabel)}</span>`
                : (unverified
                    ? `${INFO_SVG}<span>Unverified fitment &mdash; bolt pattern matches</span>`
                    : `${CHECK_SVG}<span>Fits ${escapeHtml(vehicleLabel)}</span>`);
            insertAfter.parentNode.insertBefore(badge, insertAfter.nextSibling);
        });

        updateBannerForResults(anyFits, anyChecked, anyVerified, anyUnverified);
    }

    function init() {
        insertBanner();
        insertCardBadges();
        hideActiveFilters();
        const observer = new MutationObserver(() => {
            insertCardBadges();
            hideActiveFilters();
        });
        observer.observe(document.body, { childList: true, subtree: true });
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
})();
